import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Button from '../../components/common/Button';
import { productsApi } from '../../api/products.api';
import { uploadApi } from '../../api/upload.api';
import { getProductImageUrl } from '../../utils/images';
import { PlusIcon, XMarkIcon, PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

const typeOptions = [
  { value: 'fruit', label: 'Fruit' },
  { value: 'veggie', label: 'Veggie' }
];

export default function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newSpanishName, setNewSpanishName] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageCacheKey, setImageCacheKey] = useState(Date.now());
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    product: '',
    type: 'fruit',
    name_en: '',
    names_es: []
  });

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      productsApi.getByCode(id).then((res) => {
        const product = res.data;
        setFormData({
          product: product.product || '',
          type: product.type || 'fruit',
          name_en: product.name_en || '',
          names_es: product.names_es || []
        });
        setLoading(false);
      }).catch(() => {
        setError('Failed to load product');
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleAddSpanishName = () => {
    if (newSpanishName.trim()) {
      setFormData((prev) => ({
        ...prev,
        names_es: [...prev.names_es, newSpanishName.trim()]
      }));
      setNewSpanishName('');
    }
  };

  const handleRemoveSpanishName = (index) => {
    setFormData((prev) => ({
      ...prev,
      names_es: prev.names_es.filter((_, i) => i !== index)
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSpanishName();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleRemoveSelectedImage = () => {
    setSelectedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Generate normalized_names_es from names_es
      const dataToSend = {
        ...formData,
        normalized_names_es: formData.names_es.map(name =>
          name.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '_')
        )
      };

      if (isEdit) {
        await productsApi.updateByCode(id, dataToSend);
      } else {
        await productsApi.create(dataToSend);
      }

      // Upload image if selected
      if (selectedImage && formData.product) {
        setUploadingImage(true);
        try {
          await uploadApi.uploadProductImage(selectedImage, formData.product);
          setImageCacheKey(Date.now());
        } catch (uploadErr) {
          console.error('Image upload failed:', uploadErr);
          // Product was saved, but image upload failed - don't block navigation
        }
        setUploadingImage(false);
      }

      navigate('/products');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const baseImageUrl = getProductImageUrl(formData.product);
  const imageUrl = baseImageUrl ? `${baseImageUrl}?t=${imageCacheKey}` : null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Product' : 'Create Product'}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Section */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Product Image
            </label>
            <div className="flex items-start gap-6">
              {/* Current Image */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Current Image</p>
                <div className="h-32 w-32 rounded-lg overflow-hidden bg-white flex items-center justify-center border">
                  {imageUrl && !imagePreview ? (
                    <img
                      src={imageUrl}
                      alt={formData.name_en}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span className={`text-gray-400 text-sm ${imageUrl && !imagePreview ? 'hidden' : 'flex'} flex-col items-center justify-center h-full w-full`}>
                    <PhotoIcon className="h-8 w-8 mb-1" />
                    No image
                  </span>
                </div>
                {formData.product && (
                  <p className="mt-1 text-xs text-gray-500">
                    products/{formData.product}.png
                  </p>
                )}
              </div>

              {/* New Image Preview */}
              {imagePreview && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">New Image</p>
                  <div className="h-32 w-32 rounded-lg overflow-hidden bg-white flex items-center justify-center border-2 border-primary-500 relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveSelectedImage}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-green-600">Ready to upload</p>
                </div>
              )}

              {/* Upload Button */}
              <div className="flex flex-col justify-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!formData.product}
                >
                  <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </Button>
                {!formData.product && (
                  <p className="mt-2 text-xs text-gray-500">
                    Enter product code first
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Max size: 5MB<br />PNG, JPG, or GIF
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Product Code"
              name="product"
              value={formData.product}
              onChange={handleChange}
              placeholder="e.g., apple, carrot"
              required
              disabled={isEdit}
            />
            <Select
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={typeOptions}
              required
            />
            <Input
              label="Name (English)"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              placeholder="e.g., Apple"
              required
            />
          </div>

          {/* Spanish Names Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Names (Spanish)
            </label>

            {/* Existing Spanish Names */}
            {formData.names_es.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.names_es.map((name, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => handleRemoveSpanishName(index)}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add New Spanish Name */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newSpanishName}
                onChange={(e) => setNewSpanishName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add Spanish name..."
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddSpanishName}
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Press Enter or click + to add a Spanish name
            </p>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => navigate('/products')}>
              Cancel
            </Button>
            <Button type="submit" loading={saving || uploadingImage}>
              {uploadingImage ? 'Uploading Image...' : (isEdit ? 'Update Product' : 'Create Product')}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
